import React, { useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import { AccordionProps, alpha, Chip, Typography } from '@mui/material';

import focusConfig from '@/components/Focus/configs';
import { ConfigContext } from '@/contextAPI/configContext';
import { useAppSelector } from '@/redux/hooks';
import { TitleEntity, VersionGroupedByParent } from '@/redux/slices/slice_types';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { selectVersionKeys } from '@/redux/selectors/orderPlacementSelectors';
import { generateHexFromString } from '@/_utils/colorUtils';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { TabBadge } from '@/components/_common';
import { isObjectAVersion } from '@/components/OrderPlacement/OPStep2VersionSelection/OPVersionSelections/utils';
import ScheduleSelectedAccordion from '@/components/Focus/AvailsSchedule/NewSchedule/ScheduleSelectedAccordion';
import ScheduleVersionChip from '@/components/Focus/AvailsSchedule/NewSchedule/TitleSearch/ScheduleSelectedVersions/ScheduleVersionChip';

type ScheduleTitleAccordionProps = {
  title: TitleEntity | TitleEntityV2;
  titleGroup: VersionGroupedByParent;
  selectedVersions: (TitleEntity | TitleEntityV2)['id'][];
  isExpanded: boolean;
  onExpandChange: AccordionProps['onChange'];
  onVersionClick?: (version: TitleEntity | TitleEntityV2) => void;
};

const ScheduleTitleAccordion = ({ title, titleGroup, selectedVersions, isExpanded, onExpandChange, onVersionClick }: ScheduleTitleAccordionProps) => {
  const versionColors = useContext(ConfigContext)?.versionColors || [];
  const versionsByKey = useAppSelector(selectVersionKeys);
  const focusVersionColor = focusConfig.versionColors ? focusConfig.versionColors : undefined;

  const versionTypeGroupMemo = useMemo(() => {
    // group the version type and count the number of versions per each
    const versionTypeGroup: Record<string, number> = {};

    // Helper function to process a single version
    const processVersion = (versionId: string) => {
      const version = selectTitleById(versionId);
      if (version?.entity_type === 'version') {
        const versionType: string = version.version_type || version.name;
        if (versionType) {
          versionTypeGroup[versionType] = (versionTypeGroup[versionType] ?? 0) + 1;
        }
      }
    };

    // Helper function to recursively process version structure
    const processVersions = (versionObj: VersionGroupedByParent | string) => {
      if (typeof versionObj === 'string') {
        processVersion(versionObj);
        return;
      }

      // Handle single version object directly
      if (isObjectAVersion(versionObj) && versionObj?.entity_type === 'version') {
        const versionType = versionObj.version_type || versionObj.name;
        if (versionType) {
          versionTypeGroup[versionType] = (versionTypeGroup[versionType] ?? 0) + 1;
        }
        return;
      }

      if (typeof versionObj === 'object' && versionObj !== null) {
        Object.keys(versionObj).forEach((key) => {
          processVersions(versionObj[key]);
        });
      }
    };

    // Process the versions structure
    processVersions(titleGroup);
    return versionTypeGroup;
  }, [titleGroup]);

  const selectedCount = useMemo(() => {
    return selectedVersions?.length ?? Object.keys(versionTypeGroupMemo).reduce((acc, curr) => acc + versionTypeGroupMemo[curr], 0);
  }, [versionTypeGroupMemo, selectedVersions]);

  const selectTitleById = (id: string) => {
    const titleKey = String(id).substring(1);
    return versionsByKey[titleKey] || {};
  };

  const selectVersionById = (id: string) => {
    const titleKey = String(id).substring(1);
    return versionsByKey[+titleKey] || {};
  };

  const sortVersionsByEpisodeSequence = (a: string, b: string) => {
    const sequenceOrderA = Number(selectVersionById(a).sequence_order);
    const sequenceOrderB = Number(selectVersionById(b).sequence_order);
    if (isNaN(sequenceOrderA) || isNaN(sequenceOrderB)) return 0;
    return sequenceOrderA - sequenceOrderB;
  };

  return (
    <ScheduleSelectedAccordion expanded={isExpanded} onChange={onExpandChange}>
      <ScheduleSelectedAccordion.Summary>
        <ScheduleSelectedAccordion.Content>
          <ScheduleSelectedAccordion.Title>{title.name}</ScheduleSelectedAccordion.Title>
          <ScheduleSelectedAccordion.Caption>
            {selectedCount} Episode{selectedCount > 1 ? 's' : ''} selected
          </ScheduleSelectedAccordion.Caption>
        </ScheduleSelectedAccordion.Content>
        {!isExpanded && (
          <ScheduleSelectedAccordion.Chips>
            {Object.keys(versionTypeGroupMemo)
              .sort((a, b) => a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase()))
              .map((typeKey) => {
                const color =
                  focusVersionColor && focusVersionColor[typeKey.toLowerCase()]
                    ? focusVersionColor[typeKey.toLowerCase()]
                    : versionColors[typeKey.toLowerCase()] || generateHexFromString(typeKey);
                return (
                  <GroupedVersionChip
                    key={typeKey}
                    customColor={color}
                    label={
                      <>
                        <Typography>{typeKey.toUpperCase()}</Typography>
                        <TabBadge count={selectedVersions?.length ?? versionTypeGroupMemo[typeKey]} />
                      </>
                    }
                    icon={<PolarisIcon icon="check" height={16} />}
                    onClick={(e) => e.stopPropagation()}
                  />
                );
              })}
          </ScheduleSelectedAccordion.Chips>
        )}
      </ScheduleSelectedAccordion.Summary>
      <ScheduleSelectedAccordion.Details>
        {Object.keys(titleGroup)
          .sort(sortVersionsByEpisodeSequence)
          .map((episodeKey) => {
            const episodeGroup = titleGroup[episodeKey];
            const episode = versionsByKey[episodeKey.substring(1)];

            return (
              <EpisodeListItem key={episodeKey}>
                <EpisodeDetails>
                  <Typography>E{Math.trunc(+episode?.sequence_order)}:</Typography>
                  <Typography>{episode.name}</Typography>
                </EpisodeDetails>
                <EpisodeVersions>
                  {Object.keys(episodeGroup).map((versionKey) => {
                    const version = selectVersionById(versionKey);
                    return (
                      <EpisodeVersion key={versionKey}>
                        <Typography>{version.alpha_id}</Typography>
                        <ScheduleVersionChip size="small" version={version} selectedVersions={selectedVersions} onVersionClick={onVersionClick} />
                      </EpisodeVersion>
                    );
                  })}
                </EpisodeVersions>
              </EpisodeListItem>
            );
          })}
      </ScheduleSelectedAccordion.Details>
    </ScheduleSelectedAccordion>
  );
};

export default ScheduleTitleAccordion;

const GroupedVersionChip = styled(Chip, { shouldForwardProp: (prop) => prop !== 'customColor' })<{ customColor: string }>(
  ({ theme, customColor }) => ({
    alignItems: 'center',
    border: `1px solid ${customColor}`,
    borderRadius: 20,
    padding: theme.spacing(1, 1.5),
    color: theme.palette.background.default,
    '&, &.MuiChip-root.MuiChip-filled': {
      backgroundColor: customColor
    },
    '&, &.MuiChip-root:not(.MuiChip-clickable).MuiChip-outlined': {
      backgroundColor: customColor
    },
    '& .MuiBadge-root': {
      backgroundColor: theme.palette.background.paper,
      color: customColor,
      marginLeft: 0
    },
    '&.MuiChip-root .MuiChip-icon': {
      marginLeft: 0,
      color: theme.palette.background.default
    },
    '& .MuiChip-label': {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(0, 0, 0, 1.5),
      '& .MuiTypography-root': {
        fontWeight: 600
      }
    }
  })
);

const EpisodeListItem = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'start'
}));

const EpisodeDetails = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  flex: '1 1 auto',
  '& .MuiTypography-root:first-of-type': {
    color: alpha(theme.palette.text.primary, 0.5)
  },
  '& .MuiTypography-root:last-child': {
    color: '#D9D9D9',
    fontWeight: 500
  }
}));

const EpisodeVersions = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.25),
  flexBasis: 200
}));

const EpisodeVersion = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  '& > .MuiTypography-root': {
    color: '#D9D9D9'
  },
  '& > div': {
    marginTop: theme.spacing(0),
    marginLeft: 'auto'
  }
}));
